<!-- Homepage Layout -->
<div data-testid="homepage-page" class="homepage-container min-h-screen">
  <!-- Main Content Container -->
  <div class="w-full mx-auto px-8 py-6">
    <!-- Page Header -->
    <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h2 class="homepage-title">
        {{ "homepage.title" | translate }}
      </h2>
      <div
        data-testid="homepage-tenant-workspace-banner"
        [attr.data-tenant-id]="activeTenantSnapshot().tenantId"
        class="flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/70 px-3.5 py-2 text-xs text-slate-700"
      >
        <span class="font-semibold text-sky-900" data-testid="homepage-tenant-name">
          {{ activeTenantSnapshot().tenantName }}
        </span>
        <span class="text-slate-400">|</span>
        <span data-testid="homepage-tenant-store">
          {{ "homepage.tenant_banner.store" | translate }} <strong>{{ activeTenantSnapshot().flagshipStore }}</strong>
        </span>
        <span class="text-slate-400">|</span>
        <span data-testid="homepage-tenant-warehouse">
          {{ "homepage.tenant_banner.warehouse" | translate }} <strong>{{ activeTenantSnapshot().primaryWarehouse }}</strong>
        </span>
        <span class="text-slate-400">|</span>
        <span data-testid="homepage-tenant-pricebook">
          {{ "homepage.tenant_banner.price_book" | translate }} <strong>{{ activeTenantSnapshot().priceBookCode }}</strong>
        </span>
      </div>
    </div>

    <!-- Main Layout Grid -->
    <div class="w-full">
      <div
        class="homepage-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
      >
        @for (
          section of businessCenterItems();
          track section.title;
          let idx = $index
        ) {
          <div
            class="homepage-card relative"
            [attr.data-testid]="'homepage-section-' + toTestId(section.name)"
          >
            <!-- Card Header with Icon Badge + Title -->
            <div class="homepage-card-header">
              <div
                class="homepage-icon-badge"
                [ngClass]="getSectionBadgeClass(section, idx)"
              >
                <span class="material-symbols-outlined">
                  {{ getSectionIcon(section) }}
                </span>
              </div>
              <h3 class="homepage-card-title">
                {{ section.title }}
              </h3>
            </div>

            <!-- Card Menu Items -->
            <div class="homepage-card-items">
              @for (item of getVisibleItems(section.items); track item.name) {
                @if (item.hasRoute) {
                  <a
                    [attr.data-testid]="
                      'homepage-menu-item-' + toTestId(item.name)
                    "
                    class="homepage-item-link group cursor-pointer"
                    (click)="
                      navigateToRoute(item.route); $event.preventDefault()
                    "
                  >
                    <span class="truncate">{{ item.label }}</span>
                  </a>
                } @else {
                  <div
                    [attr.data-testid]="
                      'homepage-menu-item-disabled-' + toTestId(item.name)
                    "
                    class="homepage-item-disabled group cursor-not-allowed"
                  >
                    <span class="truncate">{{ item.label }}</span>
                    <span
                      class="material-symbols-outlined text-xs text-gray-300 flex-shrink-0 ml-2"
                      >lock</span
                    >
                  </div>
                }
              }

              @if (hasMoreItems(section.items)) {
                <div class="flex items-center relative pt-1">
                  <button
                    [attr.data-testid]="
                      'homepage-show-more-' + toTestId(section.name) + '-btn'
                    "
                    class="homepage-show-more-btn inline-flex items-center gap-1.5 py-1.5 px-2 text-xs rounded-md transition-colors"
                    (click)="toggleMoreDropdown(section.title, $event)"
                  >
                    <span class="text-blue-600 text-sm font-medium">{{
                      "homepage.show_more" | translate
                    }}</span>
                    <span
                      class="bg-blue-50 text-blue-600 rounded-full px-1.5 py-0.5 text-xs font-medium"
                      >{{ getHiddenItems(section.items).length }}</span
                    >
                    <span
                      class="material-symbols-outlined text-sm text-blue-600 transition-transform"
                      [class.rotate-90]="isDropdownOpen(section.title)"
                      >chevron_right</span
                    >
                  </button>

                  <!-- Dropdown Menu -->
                  @if (isDropdownOpen(section.title)) {
                    <div
                      [attr.data-testid]="
                        'homepage-more-menu-' + toTestId(section.name)
                      "
                      class="absolute left-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-y-auto min-w-72 dropdown-menu"
                      [class.bottom-full]="
                        getDropdownPosition(section.title) === 'top'
                      "
                      [class.mb-2]="
                        getDropdownPosition(section.title) === 'top'
                      "
                      [class.top-10]="
                        getDropdownPosition(section.title) === 'bottom'
                      "
                    >
                      <div class="p-2">
                        @for (
                          item of getHiddenItems(section.items);
                          track item.name
                        ) {
                          @if (item.hasRoute) {
                            <a
                              [attr.data-testid]="
                                'homepage-more-menu-item-' +
                                toTestId(item.name)
                              "
                              class="flex items-center px-3 py-2 hover:bg-slate-50 transition-colors group text-sm rounded-md cursor-pointer text-slate-700 hover:text-blue-600"
                              (click)="
                                navigateToRoute(item.route);
                                closeDropdown();
                                $event.preventDefault()
                              "
                            >
                              <span class="truncate">{{ item.label }}</span>
                            </a>
                          } @else {
                            <div
                              [attr.data-testid]="
                                'homepage-more-menu-item-disabled-' +
                                toTestId(item.name)
                              "
                              class="flex items-center px-3 py-2 group text-sm rounded-md opacity-50 cursor-not-allowed"
                            >
                              <span class="text-gray-500 truncate">{{
                                item.label
                              }}</span>
                              <span
                                class="material-symbols-outlined text-xs text-gray-300 flex-shrink-0 ml-2"
                                >lock</span
                              >
                            </div>
                          }
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Click outside to close dropdowns -->
  @if (showMoreDropdown()) {
    <div
      data-testid="homepage-dropdown-backdrop"
      class="fixed inset-0 z-0"
      (click)="closeDropdown()"
    ></div>
  }
</div>
